import React, { useState, useEffect, useRef, useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import { SubscriptionContext } from "../context/SubscriptionContext";
import api from "../utils/Api";
import {
  CreditCard, CheckCircle, AlertTriangle, Clock,
  Users, ArrowRight, RefreshCw, CalendarClock,
  Gift, Zap, RotateCcw, Receipt, Download, Tag,
} from "lucide-react";

// ─── PRECIO CONSTANTES ────────────────────────────────────────────────────────
const PRECIO_BASE      = 18;
const EMPLEADOS_BASE   = 3;
const PRECIO_POR_EXTRA = 4;
const POLL_INTERVAL_MS = 1500;
const POLL_MAX_TRIES   = 30;

function calcTotal(employees) {
  const extra = Math.max(0, (employees || 0) - EMPLEADOS_BASE);
  return PRECIO_BASE + extra * PRECIO_POR_EXTRA;
}

// ─── Tarjeta de precio ────────────────────────────────────────────────────────
function PricingCard({ preview }) {
  if (!preview) return null;
  const extra = Math.max(0, preview.employees - EMPLEADOS_BASE);
  // Usar el total calculado por el servidor como fuente de verdad.
  // calcTotal() es el fallback local por si el campo no viene en la respuesta.
  const total = preview.total ?? calcTotal(preview.employees);

  const row = (label, value, accent) => (
    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: 14, color: accent ? "var(--clr-accent)" : "var(--clr-text)" }}>
      <span style={{ color: "var(--clr-muted)" }}>{label}</span>
      <span style={{ fontWeight: accent ? 700 : 400 }}>{value}</span>
    </div>
  );

  return (
    <div style={{ background: "var(--clr-surface)", border: "1px solid var(--clr-border)", borderRadius: 12, padding: "20px 24px", marginBottom: 24 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--clr-muted)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 14 }}>
        Resumen del plan
      </div>
      {row(`Plan base (hasta ${EMPLEADOS_BASE} empleados)`, `${PRECIO_BASE} €/mes`)}
      {extra > 0 && row(`Empleados adicionales (${extra} × ${PRECIO_POR_EXTRA} €)`, `${extra * PRECIO_POR_EXTRA} €/mes`)}
      <div style={{ borderTop: "1px solid var(--clr-border)", marginTop: 10, paddingTop: 10 }}>
        {row(`Total — ${preview.employees} empleado${preview.employees !== 1 ? "s" : ""}`, `${total} €/mes`, true)}
      </div>
    </div>
  );
}

/** Fila de metadato */
function MetaRow({ icon, label, value, accent }) {
  if (!value && value !== 0) return null;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "10px 14px",
      background: accent ? "rgba(255,122,0,.06)" : "var(--clr-surface)",
      border: `1px solid ${accent ? "rgba(255,122,0,.18)" : "var(--clr-border)"}`,
      borderRadius: 10, marginBottom: 10, fontSize: 13.5,
    }}>
      <span style={{ color: accent ? "var(--clr-accent)" : "var(--clr-muted)", flexShrink: 0 }}>{icon}</span>
      <span style={{ color: "var(--clr-muted)", flexShrink: 0 }}>{label}</span>
      <span style={{ fontWeight: 700, color: accent ? "var(--clr-accent)" : "var(--clr-text)", marginLeft: "auto" }}>{value}</span>
    </div>
  );
}

/** Panel de suscripción activa, cancelada o en trial */
function ActivePanel({ info, onCancel, onResume, onBillingPortal, loadingCancel, loadingResume, loadingPortal }) {
  const { status, endsAt, trialEndsAt, trialDaysLeft, renewsAt, onTrial, onGrace, discount } = info;

  // isCanceled: el backend ya unifica "cancelada" y "grace period" bajo status="canceled"
  // onGrace: acceso activo pero terminará en endsAt → solo mostrar si endsAt es real
  const isCanceled = status === "canceled";
  const isGrace    = onGrace && !!endsAt;   // gracia real = backend confirmó fecha de fin
  const isTrialing = onTrial && !isCanceled && !isGrace;

  // Badge: refleja el estado más relevante
  // - Grace period → ámbar "Cancelada (acceso hasta...)"
  // - Trial        → púrpura
  // - Activa       → verde
  const badgeBg    = (isCanceled || isGrace) ? "rgba(245,158,11,.1)" : isTrialing ? "rgba(124,58,237,.1)" : "rgba(16,185,129,.1)";
  const badgeColor = (isCanceled || isGrace) ? "#B45309"             : isTrialing ? "#7C3AED"             : "#059669";
  const badgeIcon  = (isCanceled || isGrace) ? <AlertTriangle size={15} /> : isTrialing ? <Gift size={15} /> : <CheckCircle size={15} />;
  const badgeLabel = (isCanceled || isGrace) ? "Cancelada"           : isTrialing ? "Período de prueba"  : "Activa";

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 7, background: badgeBg, color: badgeColor, borderRadius: 999, padding: "6px 18px", fontWeight: 700, fontSize: 13.5 }}>
          {badgeIcon} Suscripción {badgeLabel}
        </div>
      </div>

      {/* Trial activo */}
      {isTrialing && trialDaysLeft !== null && (
        <MetaRow icon={<Clock size={15} />} label="Prueba gratuita · finaliza el" value={`${trialEndsAt} (${trialDaysLeft} día${trialDaysLeft !== 1 ? "s" : ""})`} accent />
      )}

      {/* Próxima renovación: solo si está activa y NO en proceso de cancelación */}
      {renewsAt && !isCanceled && !isGrace && !isTrialing && (
        <MetaRow icon={<CalendarClock size={15} />} label="Próxima renovación" value={renewsAt} />
      )}

      {/* Acceso hasta: cuando hay fecha de fin confirmada (grace o expiración) */}
      {endsAt && (isCanceled || isGrace) && (
        <MetaRow icon={<AlertTriangle size={15} />} label="Acceso hasta el" value={endsAt} accent />
      )}

      {discount && (
        <MetaRow
          icon={<Tag size={15} />}
          label="Descuento activo"
          value={discount.percent_off ? `${discount.percent_off}% — ${discount.name}` : `${(discount.amount_off / 100).toFixed(2)} ${discount.currency?.toUpperCase()} — ${discount.name}`}
          accent
        />
      )}
      <MetaRow icon={<Zap size={15} />} label="Ciclo de facturación" value="Mensual" />

      {/* Botón cancelar: solo cuando está activa y NO hay cancelación pendiente */}
      {!isCanceled && !isGrace && !isTrialing && (
        <button className="tn-btn tn-btn--danger" style={{ width: "100%", justifyContent: "center", marginTop: 8 }} onClick={onCancel} disabled={loadingCancel}>
          {loadingCancel ? "Cancelando…" : "Cancelar suscripción"}
        </button>
      )}

      {/* Botón reactivar: cuando hay grace period activo (endsAt en el futuro) */}
      {isGrace && (
        <button className="tn-btn tn-btn--primary" style={{ width: "100%", justifyContent: "center", marginTop: 8 }} onClick={onResume} disabled={loadingResume}>
          {loadingResume ? "Reactivando…" : <><RotateCcw size={15} /> Reactivar suscripción</>}
        </button>
      )}

      {/* Botón portal de Stripe: solo si ya hay un método de pago que gestionar */}
      {!isTrialing && (
        <button className="tn-btn tn-btn--ghost" style={{ width: "100%", justifyContent: "center", marginTop: 8 }} onClick={onBillingPortal} disabled={loadingPortal}>
          {loadingPortal ? "Abriendo portal…" : <><CreditCard size={15} /> Actualizar método de pago</>}
        </button>
      )}

      {isTrialing && (
        <p style={{ textAlign: "center", fontSize: 12.5, color: "var(--clr-muted)", marginTop: 14 }}>
          Al finalizar el período de prueba se te pedirá un método de pago para continuar.
        </p>
      )}
    </div>
  );
}

/** Historial de facturas */
function InvoiceTable({ invoices, token }) {
  if (!invoices || invoices.length === 0) return null;

  return (
    <div style={{ marginTop: 32 }}>
      {/* Cabecera */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <Receipt size={17} style={{ color: "var(--clr-accent)" }} />
        <span style={{ fontWeight: 700, fontSize: 15, color: "var(--clr-text)" }}>Historial de facturación</span>
      </div>

      <div style={{ border: "1px solid var(--clr-border)", borderRadius: 12, overflow: "hidden" }}>
        {/* Thead */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 100px 48px", gap: 0, background: "var(--clr-surface)", borderBottom: "1px solid var(--clr-border)", padding: "10px 16px", fontSize: 11, fontWeight: 700, color: "var(--clr-muted)", textTransform: "uppercase", letterSpacing: 0.8 }}>
          <span>Fecha</span>
          <span>Nº factura</span>
          <span style={{ textAlign: "right" }}>Total</span>
          <span></span>
        </div>

        {/* Filas */}
        {invoices.map((inv, i) => (
          <div
            key={inv.id}
            style={{
              display: "grid", gridTemplateColumns: "1fr 1fr 100px 48px",
              alignItems: "center", padding: "12px 16px", fontSize: 13.5,
              borderBottom: i < invoices.length - 1 ? "1px solid var(--clr-border)" : "none",
              background: i % 2 === 0 ? "var(--clr-surface)" : "var(--clr-bg)",
            }}
          >
            <span style={{ color: "var(--clr-muted)" }}>{inv.date}</span>
            <span style={{ color: "var(--clr-text)", fontFamily: "monospace", fontSize: 12 }}>{inv.number || inv.id.slice(-8)}</span>
            <span style={{ fontWeight: 700, color: "var(--clr-text)", textAlign: "right" }}>{inv.total}</span>
            <span style={{ textAlign: "center" }}>
              <a
                href={inv.download_url}
                target="_blank"
                rel="noopener noreferrer"
                title="Descargar factura PDF"
                style={{ color: "var(--clr-accent)", display: "inline-flex", alignItems: "center" }}
                onClick={(e) => {
                  // Añadir el token JWT como header no es posible en <a href>,
                  // usamos fetch para conseguir el blob y forzar la descarga
                  e.preventDefault();
                  fetch(inv.download_url, { headers: { Authorization: `Bearer ${token}` } })
                    .then(r => r.blob())
                    .then(blob => {
                      const url = URL.createObjectURL(blob);
                      const a   = document.createElement('a');
                      a.href    = url;
                      a.download = `factura-${inv.number || inv.id}.pdf`;
                      a.click();
                      URL.revokeObjectURL(url);
                    })
                    .catch(() => alert("No se pudo descargar la factura."));
                }}
              >
                <Download size={16} />
              </a>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Pantalla de espera post-Stripe */
function ConfirmingOverlay({ dots }) {
  return (
    <div style={{ textAlign: "center", padding: "48px 0" }}>
      <RefreshCw size={32} style={{ animation: "spin 1s linear infinite", color: "var(--clr-accent)", marginBottom: 18 }} />
      <p style={{ fontWeight: 700, fontSize: 15, color: "var(--clr-text)", margin: "0 0 6px" }}>Confirmando tu suscripción{dots}</p>
      <p style={{ fontSize: 13, color: "var(--clr-muted)", margin: 0 }}>Estamos verificando el pago con Stripe. Esto tarda solo unos segundos.</p>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function Subscribe() {
  const { token } = useContext(AuthContext);
  const { refresh: refreshSubscription } = useContext(SubscriptionContext);

  const [subInfo,         setSubInfo]         = useState(null);
  const [preview,         setPreview]          = useState(null);
  const [loadingData,     setLoadingData]      = useState(true);
  const [loadingCheckout, setLoadingCheckout]  = useState(false);
  const [loadingCancel,   setLoadingCancel]    = useState(false);
  const [loadingResume,   setLoadingResume]    = useState(false);
  const [loadingPortal,   setLoadingPortal]    = useState(false);
  const [confirming,      setConfirming]       = useState(false);
  const [dots,            setDots]             = useState(".");
  const [message,         setMessage]          = useState("");
  const [messageType,     setMessageType]      = useState("");

  const pollRef         = useRef(null);
  const pollCount       = useRef(0);
  const dotsTimer       = useRef(null);
  const activateCalled  = useRef(false); // guard contra doble llamada (React StrictMode)
  const checkoutPending = useRef(false); // guard síncrono contra doble clic en checkout
  const lastSessionId   = useRef(null);  // detecta cambio de session_id entre flujos

  // refresh=true enriquece con Stripe (descuento, renovación exacta, facturas).
  // El polling NO refresca → cero llamadas a Stripe durante la confirmación.
  const fetchSub = async (refresh = false) => {
    const url = refresh ? "/subscribe/check?refresh=1" : "/subscribe/check";
    const res = await api.get(url, { headers: { Authorization: `Bearer ${token}` } });
    return res.data;
  };

  // Llama al endpoint de activación UNA sola vez con el session_id.
  // Devuelve true si la suscripción quedó activa inmediatamente.
  const activateSession = async (sessionId) => {
    try {
      const res = await api.post(
        "/suscripcion/activate",
        { session_id: sessionId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return !!res.data.subscribed;
    } catch {
      return false;
    }
  };

  const fetchPreview = async () => {
    const res = await api.get("/subscribe/preview", { headers: { Authorization: `Bearer ${token}` } });
    return res.data;
  };

  const loadAll = async () => {
    if (!token) return;
    setLoadingData(true);
    try {
      const [sub, prev] = await Promise.all([fetchSub(true), fetchPreview()]);
      setSubInfo(sub);
      setPreview(prev);
    } catch (err) {
      console.error("Subscribe loadAll:", err);
    } finally {
      setLoadingData(false);
    }
  };

  const stopPolling = () => { clearInterval(pollRef.current); clearInterval(dotsTimer.current); };

  const onSubscriptionActivated = async (data) => {
    stopPolling();
    // Tras activar, siempre intentamos un refresh enriquecido (descuento,
    // renovación, facturas); si Stripe falla, caemos al dato que ya tengamos.
    const finalData = await fetchSub(true).catch(() => data || null);
    if (finalData) setSubInfo(finalData);
    setConfirming(false);
    setMessage("¡Suscripción activada correctamente! Bienvenido a Turnora.");
    setMessageType("success");
    window.history.replaceState({}, "", window.location.pathname);
    refreshSubscription();
  };

  // Polling puro: solo lee /subscribe/check (BD local, sin Stripe API).
  // Se arranca DESPUÉS de llamar a activate, que ya hizo el sync con Stripe.
  const startPolling = () => {
    pollCount.current = 0;
    dotsTimer.current = setInterval(() => setDots(d => d.length >= 3 ? "." : d + "."), 500);

    pollRef.current = setInterval(async () => {
      pollCount.current += 1;
      try {
        const data = await fetchSub();
        if (data.subscribed) {
          await onSubscriptionActivated(data);
          return;
        }
      } catch { /* red caída, reintentamos */ }

      if (pollCount.current >= POLL_MAX_TRIES) {
        stopPolling();
        setConfirming(false);
        await loadAll();
        setMessage("El pago se está procesando. Si no ves tu suscripción activa en unos minutos, recarga la página o contacta con soporte.");
        setMessageType("error");
        window.history.replaceState({}, "", window.location.pathname);
      }
    }, POLL_INTERVAL_MS);
  };

  useEffect(() => {
    const params    = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const canceled  = params.get("canceled");

    if (canceled === "1") {
      setMessage("El proceso de pago fue cancelado. Puedes intentarlo de nuevo cuando quieras.");
      setMessageType("error");
      window.history.replaceState({}, "", window.location.pathname);
      loadAll();
      return;
    }

    if (sessionId) {
      // Carga el preview de precio mientras procesamos
      fetchPreview().then(setPreview).catch(() => {});
      setLoadingData(false);
      setConfirming(true);

      // Guard: React StrictMode monta el componente dos veces en desarrollo,
      // lo que dispararía activate dos veces con el mismo session_id.
      // lastSessionId detecta si es un session_id nuevo (segundo intento de pago)
      // y resetea el guard para que la nueva activación sí se ejecute.
      if (lastSessionId.current !== sessionId) {
        lastSessionId.current = sessionId;
        activateCalled.current = false;
      }
      if (!activateCalled.current) {
        activateCalled.current = true;
        activateSession(sessionId).then((activated) => {
          if (activated) {
            // Stripe confirmó la suscripción al instante → activamos sin polling
            onSubscriptionActivated(null);
          } else {
            // El sync directo falló (webhook aún en camino) → polling de BD
            startPolling();
          }
        });
      }
      // S-07: devolver cleanup para que los setInterval de polling se limpien
      // si el usuario navega fuera del componente antes de confirmar el pago.
      return () => stopPolling();
    }

    loadAll();
    return () => stopPolling();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleCheckout = async () => {
    if (checkoutPending.current) return;
    checkoutPending.current = true;
    setMessage(""); setMessageType("");
    setLoadingCheckout(true);
    try {
      const res = await api.post("/suscripcion/checkout", {}, { headers: { Authorization: `Bearer ${token}` } });
      window.location.href = res.data.url;
    } catch (err) {
      setMessage(err.response?.data?.message || "No se pudo iniciar el proceso de pago. Inténtalo de nuevo.");
      setMessageType("error");
      setLoadingCheckout(false);
      checkoutPending.current = false;
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("¿Seguro que deseas cancelar? Mantendrás acceso hasta el final de tu periodo de facturación.")) return;
    setLoadingCancel(true);
    setMessage(""); setMessageType("");
    try {
      const res = await api.post("/subscribe/cancel", {}, { headers: { Authorization: `Bearer ${token}` } });
      await loadAll();
      setMessage(res.data.message || "Suscripción cancelada.");
      setMessageType("success");
    } catch {
      setMessage("Error al cancelar la suscripción."); setMessageType("error");
    } finally { setLoadingCancel(false); }
  };

  const handleResume = async () => {
    setLoadingResume(true);
    setMessage(""); setMessageType("");
    try {
      await api.post("/subscribe/resume", {}, { headers: { Authorization: `Bearer ${token}` } });
      await loadAll();
      setMessage("Suscripción reactivada correctamente."); setMessageType("success");
    } catch (err) {
      setMessage(err.response?.data?.message || "No se pudo reactivar la suscripción."); setMessageType("error");
    } finally { setLoadingResume(false); }
  };

  const handleBillingPortal = async () => {
    setLoadingPortal(true);
    setMessage(""); setMessageType("");
    try {
      const res = await api.post("/subscribe/billing-portal", {}, { headers: { Authorization: `Bearer ${token}` } });
      window.location.href = res.data.url;
    } catch (err) {
      setMessage(err.response?.data?.message || "No se pudo abrir el portal de facturación."); setMessageType("error");
      setLoadingPortal(false);
    }
  };

  const isActive = subInfo?.subscribed;

  return (
    <div style={{ maxWidth: 580, margin: "0 auto", padding: "32px 16px" }}>
      {/* Cabecera */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: "var(--clr-text)", margin: 0 }}>Suscripción</h1>
        <p style={{ color: "var(--clr-muted)", fontSize: 14, marginTop: 4 }}>Gestiona el plan de facturación de tu empresa.</p>
      </div>

      {/* Alerta global */}
      {message && (
        <div className={`tn-alert tn-alert--${messageType === "success" ? "success" : "danger"}`} style={{ marginBottom: 20 }}>
          {message}
        </div>
      )}

      {/* Card principal */}
      <div className="tn-card" style={{ padding: "28px 28px 24px" }}>
        {loadingData ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "var(--clr-muted)" }}>
            <RefreshCw size={22} style={{ animation: "spin 1s linear infinite", marginBottom: 10 }} />
            <p style={{ margin: 0, fontSize: 14 }}>Cargando datos…</p>
          </div>
        ) : confirming ? (
          <ConfirmingOverlay dots={dots} />
        ) : isActive ? (
          <>
            <ActivePanel
              info={{
                status:        subInfo.status,
                endsAt:        subInfo.ends_at,
                trialEndsAt:   subInfo.trial_ends_at,
                trialDaysLeft: subInfo.trial_days_left,
                renewsAt:      subInfo.renews_at,
                onTrial:       subInfo.on_trial,
                onGrace:       subInfo.on_grace,
                discount:      subInfo.discount,
              }}
              onCancel={handleCancel}
              onResume={handleResume}
              onBillingPortal={handleBillingPortal}
              loadingCancel={loadingCancel}
              loadingResume={loadingResume}
              loadingPortal={loadingPortal}
            />

            {/* Historial de facturas */}
            <InvoiceTable invoices={subInfo.invoices} token={token} />
          </>
        ) : (
          <>
            <PricingCard preview={preview} />

            <ul style={{ listStyle: "none", padding: 0, margin: "0 0 24px", display: "flex", flexDirection: "column", gap: 8 }}>
              {["Fichajes en tiempo real", "Gestión de incidencias y ausencias", "Informes PDF descargables", "Soporte por email incluido"].map(feat => (
                <li key={feat} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
                  <CheckCircle size={15} style={{ color: "var(--clr-accent)", flexShrink: 0 }} />{feat}
                </li>
              ))}
            </ul>

            <button
              className="tn-btn tn-btn--primary tn-btn--lg"
              style={{ width: "100%", justifyContent: "center" }}
              onClick={handleCheckout}
              disabled={loadingCheckout}
            >
              {loadingCheckout
                ? <><RefreshCw size={16} style={{ animation: "spin 1s linear infinite" }} /> Redirigiendo a Stripe…</>
                : <><CreditCard size={16} /> Suscribirse ahora <ArrowRight size={16} /></>}
            </button>

            <p style={{ textAlign: "center", fontSize: 12, color: "var(--clr-muted)", marginTop: 12 }}>
              Pago seguro gestionado por Stripe · Sin permanencia · Cupones admitidos
            </p>
          </>
        )}
      </div>
    </div>
  );
}